import CloseIcon from '@mui/icons-material/Close'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ModalOverlay } from '../ModalOverlay'
import LabOrderDetailView, { LabStatusBadge } from './LabOrderDetailView'
import ClinicOrderPanels from './ClinicOrderPanels'
import {
  orderStatusStyles,
  paymentStatusStyles,
  orderMatchesKey,
  type LabOrderDetail,
  type LabOrderListItem,
} from './labOrderTypes'

type LabOrderDetailModalProps = {
  open: boolean
  orderId: string | null
  orderNumber?: string | null
  listOrder?: LabOrderListItem | null
  order: LabOrderDetail | null
  loading: boolean
  onClose: () => void
  onOrderUpdated?: (order: LabOrderDetail) => void
}

export default function LabOrderDetailModal({
  open,
  orderId,
  orderNumber,
  listOrder = null,
  order,
  loading,
  onClose,
  onOrderUpdated,
}: LabOrderDetailModalProps) {
  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  const displayOrder =
    order && orderMatchesKey(order, orderId) ? order : null

  const headerOrder =
    displayOrder ??
    (listOrder && orderMatchesKey(listOrder, orderId) ? listOrder : null)

  const headerCaseId = headerOrder?.clinicCaseId || ''
  const headerOrderNumber =
    headerOrder?.id ?? orderNumber ?? listOrder?.id ?? '…'
  const headerStatus = headerOrder?.orderStatus
  const headerPayment = headerOrder?.payment
  const headerLabLine = headerOrder
    ? `${headerOrder.labName} · ${headerOrder.finalTotal}`
    : null

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-order-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="relative my-4 flex w-full max-w-5xl flex-col rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800 sm:px-6">
          <div className="min-w-0 flex-1">
            <h2
              id="lab-order-detail-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              {headerCaseId ? `Case ${headerCaseId}` : `Order #${headerOrderNumber}`}
            </h2>
            {headerStatus && headerPayment ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <LabStatusBadge
                  label={headerStatus}
                  className={orderStatusStyles[headerStatus]}
                />
                <LabStatusBadge
                  label={headerPayment}
                  className={paymentStatusStyles[headerPayment]}
                />
                {headerLabLine ? (
                  <span className="text-xs text-slate-500">{headerLabLine}</span>
                ) : null}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {loading ? (
            <div className="space-y-4 py-8">
              {listOrder && orderMatchesKey(listOrder, orderId) ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {listOrder.case}
                  </p>
                  <p className="mt-1 text-slate-600 dark:text-slate-400">
                    Patient: {listOrder.patient || '—'}
                    {listOrder.patientMeta ? ` · ${listOrder.patientMeta}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {listOrder.labName}
                    {listOrder.orderDate ? ` · Ordered ${listOrder.orderDate}` : ''}
                    {listOrder.dueDate ? ` · Due ${listOrder.dueDate}` : ''}
                  </p>
                </div>
              ) : null}
              <p className="text-center text-sm text-slate-500">
                Loading full order details…
              </p>
            </div>
          ) : displayOrder ? (
            <div className="space-y-5">
              <ClinicOrderPanels
                order={displayOrder}
                onUpdated={(updated) => onOrderUpdated?.(updated)}
              />
              <LabOrderDetailView order={displayOrder} />
            </div>
          ) : (
            <p className="py-12 text-center text-sm text-slate-500">
              Could not load order details.
            </p>
          )}
        </div>

        {displayOrder?.treatmentId ? (
          <footer className="flex shrink-0 justify-end gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-6">
            <Link
              to={`/client/treatment-diagnose/add-treatment/lab-order/${displayOrder.treatmentId}`}
              className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5"
              onClick={onClose}
            >
              Open in treatment
            </Link>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
            >
              Close
            </button>
          </footer>
        ) : (
          <footer className="flex shrink-0 justify-end border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
            >
              Close
            </button>
          </footer>
        )}
      </div>
    </ModalOverlay>
  )
}
